import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { shorten } from "@backpacker/number";
import { Color, Radius } from "@glossy";
import { content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { ResponsiveStream } from "@nivo/stream";
import { fetchStreamId, getCustomTheme, getDataFromSvg } from "./data";

export type Serie = {
  [key: string]: number;
};

interface IProps {
  series: Serie[];
  keys: string[];
  onClickStream?: (
    uid: string | number,
    range: { from: AbstractDate; to: AbstractDate }
  ) => void;
  className?: string;
  offsetType?: "none" | "silhouette";
  curve?: "basis" | "linear";
  locale?: Locale;
  colors?: string[];
  range?: Range;
  total?: number;
  theme: GlossyTheme;
  legendSeries?: any;
}

const ChartWrapper = styled.div<{ clickable: boolean }>`
  height: 100%;
  width: 100%;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  > div > div > div > div {
    color: ${Color.White} !important;
    background-color: ${Color.Black} !important;
    font-size: 14px !important;
    padding: 16px !important;
    border-radius: ${Radius.Medium} !important;
  }
`;

const StreamchartChart = ({
  series,
  keys,
  offsetType = "silhouette",
  curve = "basis",
  locale = Locale.English,
  colors,
  onClickStream,
  theme,
  legendSeries,
}: IProps) => {
  const chartRef = useRef(null);
  const customTheme = getCustomTheme(theme);

  const handleClick = useCallback(
    (e, chartData) => {
      const streamId = fetchStreamId(e, chartData, legendSeries);
      return streamId && onClickStream(streamId.toLowerCase(), null); //TODO: find a way to get the date
    },
    [legendSeries, onClickStream]
  );

  useEffect(() => {
    let isUnmounted = false;
    const element = chartRef && chartRef.current;
    if (element) {
      setTimeout(() => {
        const svg = element.querySelector("svg");
        getDataFromSvg(svg).then((chartData) => {
          if (isUnmounted) {
            return;
          }
          svg.onclick = (e) => onClickStream && handleClick(e, chartData);
        });
        return () => {
          isUnmounted = true;
        };
      }, 10);
    }
  }, [handleClick, onClickStream]);

  return (
    <ChartWrapper ref={chartRef} clickable={!!onClickStream}>
      <ResponsiveStream
        data={series}
        keys={keys}
        theme={customTheme}
        colors={colors}
        margin={{ bottom: 76, left: offsetType === "silhouette" ? 3 : 30 }}
        order="ascending"
        axisBottom={{
          tickSize: 5,
          tickPadding: 12,
          tickRotation: -28,
        }}
        axisLeft={
          offsetType === "silhouette"
            ? null
            : {
                tickSize: 5,
                tickPadding: 5,
                format: (tick) => shorten(locale, tick as number),
              }
        }
        curve={curve}
        offsetType={offsetType}
        borderColor={content({ theme }, "tertiary")}
        animate={true}
        motionStiffness={90}
        motionDamping={15}
      />
    </ChartWrapper>
  );
};

export default StreamchartChart;
